/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
/**
 * Shared print utilities — hospital letterhead header + footer for all reports
 */

export interface HospitalInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  regNo: string;
  gstin?: string;
}

// Cache hospital info after first fetch
let _hospitalInfo: HospitalInfo | null = null;

export async function getHospitalInfo(): Promise<HospitalInfo> {
  if (_hospitalInfo) return _hospitalInfo;
  try {
    const r = await fetch('/api/settings', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    const settings = await r.json();
    const get = (key: string) => {
      const entry = Array.isArray(settings) ? settings.find((s: any) => s.key === key) : settings[key];
      if (!entry) return '';
      const val = entry.value ?? entry;
      return typeof val === 'string' ? val.replace(/^"|"$/g, '') : String(val);
    };
    _hospitalInfo = {
      name: get('hospital_name') || 'City Hospital & Research Centre',
      address: get('hospital_address') || '',
      phone: get('hospital_phone') || '',
      email: get('hospital_email') || '',
      regNo: get('hospital_reg_no') || '',
      gstin: get('hospital_kra_pin') || get('hospital_gstin') || '',
    };
  } catch {
    _hospitalInfo = { name: 'City Hospital & Research Centre', address: '', phone: '', email: '', regNo: '' };
  }
  return _hospitalInfo!;
}

export function printHeaderHtml(h: HospitalInfo, docTitle: string): string {
  return `
    <div class="print-header">
      <div class="hospital-name">${h.name}</div>
      <div class="hospital-sub">${[h.address, h.phone, h.email].filter(Boolean).join(' | ')}</div>
      ${h.regNo ? `<div class="hospital-meta">Reg No: ${h.regNo}${h.gstin ? ' | KRA PIN: ' + h.gstin : ''}</div>` : ''}
      <div class="doc-title">${docTitle}</div>
      <hr class="header-line"/>
    </div>
  `;
}

export function printFooterHtml(note?: string): string {
  const now = new Date().toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });
  return `
    <div class="print-footer">
      <hr class="footer-line"/>
      <div class="footer-row">
        <span>${note || 'This is a computer-generated document.'}</span>
        <span>Printed: ${now}</span>
      </div>
      <div class="footer-sig">
        <div class="sig-box"><div class="sig-line"></div><div>Authorised Signatory</div></div>
      </div>
    </div>
  `;
}

export const PRINT_BASE_CSS = `
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
  body { font-size: 11px; color: #111; background: #fff; padding: 18px 22px; }
  .print-header { text-align: center; margin-bottom: 10px; }
  .hospital-name { font-size: 18px; font-weight: 700; color: #1e3a5f; letter-spacing: 0.5px; }
  .hospital-sub { font-size: 10px; color: #555; margin-top: 2px; }
  .hospital-meta { font-size: 9px; color: #888; margin-top: 2px; }
  .doc-title { font-size: 13px; font-weight: 700; margin: 8px 0 4px; text-transform: uppercase; letter-spacing: 1px; color: #1e3a5f; }
  .header-line { border: none; border-top: 2px solid #1e3a5f; margin: 4px 0 12px; }
  .print-footer { margin-top: 24px; }
  .footer-line { border: none; border-top: 1px solid #ccc; margin-bottom: 6px; }
  .footer-row { display: flex; justify-content: space-between; font-size: 9px; color: #666; }
  .footer-sig { margin-top: 30px; display: flex; justify-content: flex-end; }
  .sig-box { text-align: center; font-size: 10px; color: #555; }
  .sig-line { border-top: 1px solid #333; width: 150px; margin-bottom: 3px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; }
  th { background: #1e3a5f; color: #fff; padding: 5px 8px; font-size: 10px; text-align: left; }
  td { padding: 4px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
  tr:nth-child(even) td { background: #f9fafb; }
  .label { font-weight: 600; color: #444; }
  .section { margin: 10px 0; }
  .section h4 { font-size: 11px; font-weight: 700; color: #1e3a5f; border-bottom: 1px solid #ddd; padding-bottom: 3px; margin-bottom: 6px; text-transform: uppercase; }
  .row { display: flex; gap: 12px; margin: 4px 0; }
  .field { flex: 1; }
  .field .lbl { font-size: 9px; color: #888; text-transform: uppercase; }
  .field .val { font-size: 11px; font-weight: 600; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 9999px; font-size: 9px; font-weight: 700; }
  .badge-blue { background: #dbeafe; color: #1d4ed8; }
  .badge-green { background: #dcfce7; color: #15803d; }
  .badge-red { background: #fee2e2; color: #dc2626; }
  .badge-amber { background: #fef3c7; color: #d97706; }
  .watermark { position: fixed; bottom: 40%; left: 50%; transform: translateX(-50%) rotate(-30deg); font-size: 60px; color: rgba(0,0,0,0.04); pointer-events: none; z-index: 0; font-weight: 900; white-space: nowrap; }
`;

export async function openPrintWindow(contentHtml: string, docTitle: string, noteText?: string): Promise<void> {
  const h = await getHospitalInfo();
  const html = `<!DOCTYPE html><html><head><title>${docTitle} - ${h.name}</title>
    <style>${PRINT_BASE_CSS}</style></head>
    <body>
      <div class="watermark">${h.name}</div>
      ${printHeaderHtml(h, docTitle)}
      ${contentHtml}
      ${printFooterHtml(noteText)}
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),800);}<\/script>
    </body></html>`;
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (w) { w.document.write(html); w.document.close(); }
}
